"use client"

import { useState, useEffect } from "react"
import Modal from "../modules/Modal/Moadal"
import Button from "../modules/Button/Button"
import Spinner from "../../spinner"
import Input from "../modules/Input/Input"
import { formatDate } from "../../../utils/helper"
import styles from "../../../styles/patient-page-modules/requestDetailModal.module.css"
import Badge from "../modules/Badge/Badge"
import { getTicketEvents, createTicketEvent } from "../../../services/inventory/detail"

const RequestDetailModal = ({ isOpen, onClose, currentPatient, requestData, ticket, onForward }) => {
  const [events, setEvents] = useState([])
  const [isEventLoading, setIsEventLoading] = useState(true)
  const [isAddEventLoading, setIsAddEventLoading] = useState(false)
  const [isForwarding, setIsForwarding] = useState(false)

  const deviceInfo = {
    imei: requestData?.gatewayId || ticket?.serialNumber || "N/A",
    name: requestData?.deviceType || ticket?.type || "N/A",
  }

  // 🔹 Fetch events from API
  const fetchEvents = async () => {
    if (!ticket?.tenovi_ticket_id) {
      setEvents([])
      setIsEventLoading(false)
      return
    }

    setIsEventLoading(true)
    try {
      const { success, data } = await getTicketEvents(ticket.tenovi_ticket_id)
      setEvents(success ? data || [] : [])
    } catch (err) {
      console.error(err)
      setEvents([])
    } finally {
      setIsEventLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) fetchEvents()
    if (!isOpen) setEvents([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.tenovi_ticket_id, isOpen])

  // 🔹 Add Event
  const handleAddEvent = async (values) => {
    if (!ticket?.tenovi_ticket_id) return
    setIsAddEventLoading(true)
    try {
      const payload = { log_description: values.log_description }
      const res = await createTicketEvent(ticket.tenovi_ticket_id, payload)
      if (res && res.success) {
        setEvents((prev) => [res.data, ...prev]) // prepend new event
      } else {
        alert(res?.message || "Failed to add event")
      }
    } catch (err) {
      console.error(err)
      alert("Error adding event")
    } finally {
      setIsAddEventLoading(false)
    }
  }

  const handleSubmit = (callback) => (e) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const values = { log_description: formData.get("log_description") }
    callback(values)
    e.target.reset() // reset form after submit
  }

  // Determine status labels/variants
  const getStatusVariant = (status) => {
    if (!status) return "default"
    switch (String(status).toLowerCase()) {
      case "resolved":
        return "success"
      case "closed":
        return "danger"
      case "pending":
        return "warning"
      case "canceled":
        return "danger"
      case "forwarded":
        return "success"
      case "open":
        return "warning"
      default:
        return "default"
    }
  }

  const getStatusLabel = () => {
    if (ticket?.resolved) return "Resolved"
    if (ticket?.closed) return "Closed"
    return ticket?.status ? String(ticket.status).charAt(0).toUpperCase() + String(ticket.status).slice(1) : "Open"
  }

  // --- Forward button logic ---
  // Forward should be active only when ticket.status === "pending" (case-insensitive)
  // Forward should be disabled when ticket.status === "canceled" (or resolved/closed)
  const statusStr = ticket?.status ? String(ticket.status).toLowerCase() : ""
  const forwardAllowed = statusStr === "pending"
  const forwardDisabled = !ticket?.tenovi_ticket_id || !forwardAllowed || statusStr === "canceled" || ticket?.resolved || ticket?.closed

  const handleForwardClick = async () => {
    if (forwardDisabled) return
    setIsForwarding(true)

    // If parent provided a handler, call it (preferred)
    if (typeof onForward === "function") {
      try {
        await onForward(ticket)
        // parent can decide to update ticket status — refresh events anyway
        await fetchEvents()
      } catch (err) {
        console.error("onForward handler failed:", err)
        alert("Forward action failed")
      } finally {
        setIsForwarding(false)
      }
      return
    }

    // Fallback: create a ticket event with event_type 'forward' — adjust payload to match your API
    try {
      const payload = { log_description: "Ticket forwarded", event_type: "forward" }
      const res = await createTicketEvent(ticket.tenovi_ticket_id, payload)
      if (res && res.success) {
        // prepend event and refresh
        setEvents((prev) => [res.data, ...prev])
        // Optionally, you might want to update ticket status locally or via API — that is not implemented here
      } else {
        alert(res?.message || "Forward failed")
      }
    } catch (err) {
      console.error("Forward failed:", err)
      alert("Forward failed")
    } finally {
      setIsForwarding(false)
    }
  }

  const modalFooter = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {/* Forward button: enabled only when pending */}
      <Button
        variant={forwardAllowed ? "primary" : "secondary"}
        onClick={handleForwardClick}
        disabled={forwardDisabled || isForwarding}
        loading={isForwarding}
        title={
          ticket?.tenovi_ticket_id
            ? forwardAllowed
              ? "Forward ticket"
              : statusStr === "canceled"
              ? "Cannot forward a canceled ticket"
              : "Forward not allowed for this status"
            : "Ticket missing Tenovi Ticket ID"
        }
      >
        Forward
      </Button>

      <Button variant="ghost" onClick={onClose}>
        Close
      </Button>
    </div>
  )

  return (
    <Modal title="Ticket Events" isOpen={isOpen} onClose={onClose} footer={modalFooter} width="900px" height="auto">
      <div className={styles.request_wrapper}>
        {/* Device Info */}
        <div className={styles.request_header}>
          <div className={styles.info}>
            <h4>Device Information</h4>
            <div className={styles.details}>
              <div>
                <h5>Gateway ID / IMEI</h5>
                <p>{deviceInfo.imei}</p>
              </div>
              <div>
                <h5>Device Type</h5>
                <p>{deviceInfo.name}</p>
              </div>
              <div>
                <Badge variant={getStatusVariant(getStatusLabel())}>{getStatusLabel()}</Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Event Logs */}
        <div className={styles.event_logs}>
          <div className={styles.logs}>
            <h3>Event Logs</h3>

            {isEventLoading ? (
              <div className={styles.spinnerWrapper}>
                <Spinner />
              </div>
            ) : !ticket?.tenovi_ticket_id ? (
              <p>This ticket does not have a Tenovi Ticket ID.</p>
            ) : events.length > 0 ? (
              <div className={styles.table_container}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.tableHeader}>Created By</th>
                      <th className={styles.tableHeader}>Time Stamp</th>
                      <th className={styles.tableHeader}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event) => (
                      <tr key={event.id}>
                        <td className={styles.tableCell}>{event.created_by || "_"}</td>
                        <td className={styles.tableCell}>{formatDate(event.created_at)}</td>
                        <td className={styles.tableCell}>{event.log_description || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No events found for this ticket.</p>
            )}
          </div>
        </div>

        {/* Add Event Form */}
        {ticket?.tenovi_ticket_id && (
          <div className={styles.request_header}>
            <form className={styles.addEventFormContainer} onSubmit={handleSubmit(handleAddEvent)}>
              <h4>Add Event</h4>
              <section>
                <section className={styles.input_wrapper}>
                  <div className={styles.input_group}>
                    <div className={styles.input_field}>
                      <Input label="Event Description" type="text" name="log_description" required fullWidth />
                    </div>
                  </div>
                </section>
                <section>
                  <Button size="default" type="submit" loading={isAddEventLoading} disabled={isAddEventLoading}>
                    Add
                  </Button>
                </section>
              </section>
            </form>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default RequestDetailModal
